// Safety Service
// Following existing patterns from MessagingService and ProfileService

import { apiClient } from '../../utils/Authentication/apiClient';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import {
  BlockedUser,
  UserReport,
  BlockUserRequest,
  ReportUserRequest,
  SafetySettings,
  ReportType,
  ReportStatus
} from '../../types/safety';

class SafetyService {
  private baseUrl = config.api.baseUrl;

  private getAuthToken(): string {
    return authInterceptor.getAccessToken() || '';
  }

  // Block User Methods
  async blockUser(request: BlockUserRequest): Promise<BlockedUser> {
    try {
      const response = await apiClient.post('/safety/block', request);

      if (!response.ok) {
        throw new Error(`Failed to block user: ${response.statusText}`);
      }

      const { data } = await response.json();
      return {
        ...data,
        blockedAt: new Date(data.blockedAt)
      };
    } catch (error) {
      console.error('Error blocking user:', error);
      throw error;
    }
  }

  async unblockUser(userId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/safety/unblock/${userId}`);

      if (!response.ok) {
        throw new Error(`Failed to unblock user: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      throw error;
    }
  }

  async getBlockedUsers(): Promise<BlockedUser[]> {
    try {
      const response = await apiClient.get('/safety/blocked-users');

      if (!response.ok) {
        throw new Error(`Failed to fetch blocked users: ${response.statusText}`);
      }

      const { data } = await response.json();
      // The endpoint returns the array itself, not { blockedUsers: [...] }.
      // Reading a property that was never there meant this always resolved to
      // an empty list, which is part of why the page fell back to mock data.
      const rows = Array.isArray(data) ? data : (data?.blockedUsers ?? []);
      return rows.map((user: any) => ({
        ...user,
        blockedAt: new Date(user.blockedAt)
      }));
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      throw error;
    }
  }

  // Report User Methods
  async reportUser(request: ReportUserRequest): Promise<UserReport> {
    try {
      const response = await apiClient.post('/safety/report', request);

      if (!response.ok) {
        throw new Error(`Failed to report user: ${response.statusText}`);
      }

      const { data } = await response.json();
      return {
        ...data,
        createdAt: new Date(data.createdAt),
        reviewedAt: data.reviewedAt ? new Date(data.reviewedAt) : undefined
      };
    } catch (error) {
      console.error('Error reporting user:', error);
      throw error;
    }
  }

  async getUserReports(): Promise<UserReport[]> {
    try {
      const response = await apiClient.get('/safety/reports');

      if (!response.ok) {
        throw new Error(`Failed to fetch reports: ${response.statusText}`);
      }

      const { data } = await response.json();
      // Same shape mismatch as getBlockedUsers above.
      const rows = Array.isArray(data) ? data : (data?.reports ?? []);
      return rows.map((report: any) => ({
        ...report,
        createdAt: new Date(report.createdAt),
        reviewedAt: report.reviewedAt ? new Date(report.reviewedAt) : undefined
      }));
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  }

  // Photo Validation
  async validatePhoto(photoId: string): Promise<void> {
    try {
      const response = await apiClient.post('/safety/validate-photo', { photoId });

      if (!response.ok) {
        throw new Error(`Failed to submit photo for validation: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error submitting photo for validation:', error);
      throw error;
    }
  }

  // Safety Settings
  async getSafetySettings(): Promise<SafetySettings> {
    try {
      const response = await apiClient.get('/safety/settings');

      if (!response.ok) {
        throw new Error(`Failed to fetch safety settings: ${response.statusText}`);
      }

      const { data } = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching safety settings:', error);
      throw error;
    }
  }

  async updateSafetySettings(settings: Partial<SafetySettings>): Promise<SafetySettings> {
    try {
      const response = await apiClient.put('/safety/settings', settings);

      if (!response.ok) {
        throw new Error(`Failed to update safety settings: ${response.statusText}`);
      }

      const { data } = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating safety settings:', error);
      throw error;
    }
  }

  // Mock Methods for Development
  async mockGetBlockedUsers(): Promise<BlockedUser[]> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return [
      {
        id: 'block-1',
        userId: 'current-user',
        blockedUserId: 'user-1',
        blockedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
        reason: 'Inappropriate behavior',
        user: {
          id: 'user-1',
          name: 'John Doe',
          firstName: 'John',
          photo: '/images/placeholder-user.jpg',
          age: 28
        }
      }
    ];
  }

  async mockGetUserReports(): Promise<UserReport[]> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return [
      {
        id: 'report-1',
        reporterId: 'current-user',
        reportedUserId: 'user-2',
        reportType: 'inappropriate_content',
        reason: 'Inappropriate photos',
        description: 'User has inappropriate photos in their profile',
        status: 'pending',
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        reportedUser: {
          id: 'user-2',
          name: 'Jane Smith',
          firstName: 'Jane',
          photo: '/images/placeholder-user.jpg'
        }
      }
    ];
  }

  async mockBlockUser(request: BlockUserRequest): Promise<BlockedUser> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      id: `block-${Date.now()}`,
      userId: 'current-user',
      blockedUserId: request.userId,
      blockedAt: new Date(),
      reason: request.reason
    };
  }

  async mockReportUser(request: ReportUserRequest): Promise<UserReport> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      id: `report-${Date.now()}`,
      reporterId: 'current-user',
      reportedUserId: request.reportedUserId,
      reportType: request.reportType,
      reason: request.reason,
      description: request.description,
      status: 'pending',
      createdAt: new Date()
    };
  }
}

export const safetyService = new SafetyService();






